<template>
    <div class="afu-pt-7">
        <h3 class="afu-text-4xl afu-text-secondary afu-mb-2 afu-font-semibold">
            {{ $_seekerFieldByCode('delete_account').label || $t('account.delete_account') }}
        </h3>
        <div v-if="deleteContent"> 
            <div v-html="deleteContent" />
        </div>
        <div v-else>
            <p class="afu-text-black afu-mb-2">
                <strong>{{ $t('account.delete_account_info.header') }}</strong>
            </p>

            <p class="afu-text-black afu-mb-2">
                {{ $t('account.delete_account_info.body') }}
            </p>
            <ul class="afu-list-disc afu-mb-2">
                <li class="afu-text-black">
                    {{ $t('account.delete_account_info.list[0]') }}
                </li>
                <li class="afu-text-black">
                    {{ $t('account.delete_account_info.list[1]') }}
                </li>
                <li class="afu-text-black">
                    {{ $t('account.delete_account_info.list[2]') }}
                </li>
                <li class="afu-text-black">
                    {{ $t('account.delete_account_info.list[3]') }}
                </li>
            </ul>

            <p class="afu-text-black afu-mb-7">
                {{ $t('account.delete_account_info.footer') }}
            </p>
        </div>
        <C1Alert
            v-if="isStateDeleted || !!response.message"
            :variant="response.variant"
        >
            <div>
                <strong>{{ response.message_title }}</strong>
            </div>
            {{ response.message }}
        </C1Alert>

        <div
            v-if="isStateDeleting"
            class="afu-border afu-border-solid afu-border-danger-400 afu-rounded-card afu-p-3"
        >
            <h6 class="afu-text-danger-400 afu-mb-3">
                {{ $t('account.delete_confirmation') }}
            </h6>

            <C1Button
                v-if="!loader.request_delete_account"
                variant="outline-secondary"
                class="afu-mr-2"
                :disabled="$_seekerFieldByCode('delete_account').read_only"
                @click="resetStatus()"
            >
                {{ $t('label.cancel') }}
            </C1Button>

            <C1Button
                variant="outline-primary"
                :disabled="loader.delete_account || $_seekerFieldByCode('delete_account').read_only"
                :is-busy="loader.delete_account"
                @click="deleteAccount()"
            >
                {{ $t('label.confirm_remove') }}
            </C1Button>
        </div>

        <C1Button
            v-if="isEmptyState && canDeleteAccount"
            variant="outline-danger"
            :disabled="$_seekerFieldByCode('delete_account').read_only"
            @click="changeStatusToDeleting()"
        >
            {{ $t('label.delete_my_account') }}
        </C1Button>
    </div>
</template>

<script>
    import has from 'lodash/has';
    import { mapState, mapActions, mapGetters } from 'vuex';
    import Cookie from '@/services/cookie';
    import C1Button from '@/components/C1Components/C1Button';
    import C1Alert from '@/components/C1Components/C1Alert';

    export default {
        name: 'SettingDeleteAccount',

        components: {
            C1Alert,
            C1Button,
        },

        data() {
            return {
                response: {
                    message_title: '',
                    message: '',
                    variant: '',
                },
                status: '',
                loader: {
                    delete_account: false,
                },
                canDeleteAccount: true,
                deleteContent: '',
            };
        },

        computed: {
            ...mapState({
                $_apiMessage: state => state.apiMessage,
            }),

            ...mapGetters('site', {
                $_seekerFieldByCode: 'seekerFieldByCode',
            }),

            isEmptyState() {
                return this.status === '';
            },

            isStateDeleting() {
                return this.status === 'deleting';
            },

            isStateDeleted() {
                return this.status === 'deleted';
            },
        },
        
        created() {
            const candidateDeletionOption = window?.afConfig?.candidate_info_deletion_options;
            if (candidateDeletionOption.toLowerCase() == 'information only') {
                this.canDeleteAccount = false;
                this.deleteContent = window?.afConfig?.candidate_request_delete_info;
            }
            if (candidateDeletionOption.toLowerCase() == 'delete') {
                this.canDeleteAccount = true;
                this.deleteContent = window?.afConfig?.candidate_self_delete_info;
            }
        },

        methods: {
            ...mapActions('me', {
                $_deleteAccount: 'deleteAccount',
            }),
            
            deleteAccount() {
                this.loader.delete_account = true;
                this.$_deleteAccount()
                    .then(() => {
                        this.response.message_title = this.$t(
                            'account.delete_success_title',
                        );
                        this.response.message = this.$t(
                            'account.delete_success_message',
                        );
                        this.response.variant = 'success';

                        this.changeStatusToDeleted();

                        Cookie.deleteToken();

                        setTimeout(() => {
                            window.location.href = '/';
                        }, 5000);
                    })
                    .catch(error => {
                        const errorCode = has(error, 'response.data.error')
                            ? error.response.data.error
                            : 'ServerErrorException';
                        this.response.message_title = 'Deleting failed';
                        this.response.message = this.$_apiMessage[errorCode];
                        this.response.variant = 'danger';
                    })
                    .finally(() => {
                        this.loader.delete_account = false;
                    });
            },

            changeStatusToDeleting() {
                this.status = 'deleting';
            },

            changeStatusToDeleted() {
                this.status = 'deleted';
            },

            resetStatus() {
                this.status = '';
            },
        },
    };
</script>
